import type { Condition, FormData, FormSchema, FormField, GroupCondition, LeafCondition } from './types.js';
import { DISPLAY_ONLY_TYPES } from './types.js';

/**
 * ประเมินเงื่อนไข showIf
 *
 * ฟังก์ชันนี้ต้องให้ผลเหมือนกันเป๊ะ ๆ ทั้งฝั่ง LIFF (ตัดสินว่าจะแสดงฟิลด์ไหม)
 * และฝั่ง Worker (ตัดสินว่าจะบังคับ required ไหม) — ถ้าไม่ตรงกันจะเกิดกรณี
 * "หน้าจอให้ผ่านแต่เซิร์ฟเวอร์ปฏิเสธ" ซึ่ง debug ยากมากในภาคสนาม
 */

function isGroup(c: Condition): c is GroupCondition {
  return 'all' in c || 'any' in c;
}

export function evalCondition(condition: Condition | undefined, data: FormData): boolean {
  if (!condition) return true;

  if (isGroup(condition)) {
    if (condition.all) return condition.all.every((c) => evalCondition(c, data));
    if (condition.any) return condition.any.some((c) => evalCondition(c, data));
    return true;
  }

  const leaf = condition as LeafCondition;
  const value = data[leaf.field];

  if (leaf.truthy !== undefined) {
    return isTruthy(value) === leaf.truthy;
  }
  if (leaf.eq !== undefined) return looseEq(value, leaf.eq);
  if (leaf.neq !== undefined) return !looseEq(value, leaf.neq);
  if (leaf.in !== undefined) return leaf.in.some((v) => looseEq(value, v));
  if (leaf.nin !== undefined) return !leaf.nin.some((v) => looseEq(value, v));

  const n = toNumber(value);
  if (leaf.gt !== undefined) return n !== null && n > leaf.gt;
  if (leaf.gte !== undefined) return n !== null && n >= leaf.gte;
  if (leaf.lt !== undefined) return n !== null && n < leaf.lt;
  if (leaf.lte !== undefined) return n !== null && n <= leaf.lte;

  // ไม่มีตัวดำเนินการเลย = ถือว่าผ่าน (validate จะดักตอน publish)
  return true;
}

function isTruthy(v: unknown): boolean {
  if (v === null || v === undefined || v === '') return false;
  if (v === false || v === 0) return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/**
 * เทียบแบบผ่อนปรน: checkbox ที่เก็บเป็น 0/1 ต้องเทียบกับ true/false ได้
 * และตัวเลขที่มาจาก input ในรูปสตริงต้องเทียบกับตัวเลขได้
 */
function looseEq(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || a === undefined) return b === null;

  if (typeof b === 'boolean') return Boolean(toBoolean(a)) === b;
  if (typeof b === 'number') {
    const n = toNumber(a);
    return n !== null && n === b;
  }
  if (typeof b === 'string') {
    // multiselect: ถือว่า "เท่ากับ" ถ้ามีค่านั้นอยู่ในรายการ
    if (Array.isArray(a)) return a.map(String).includes(b);
    return String(a) === b;
  }
  return false;
}

function toBoolean(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  if (v === 1 || v === '1' || v === 'true') return true;
  if (v === 0 || v === '0' || v === 'false') return false;
  return null;
}

function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  if (Array.isArray(v)) return v.length;
  return null;
}

export { toNumber as coerceNumber, toBoolean as coerceBoolean, isTruthy };

/* -------------------------------------------------------------- visibility */

export interface VisibleField {
  field: FormField;
  sectionKey: string;
}

/**
 * คืนรายการฟิลด์ที่ต้องแสดง/ตรวจ ตามคำตอบปัจจุบัน
 *
 * @param hideSensitive ซ่อน section ที่ sensitive = true (เมื่อยังไม่มีความยินยอมข้อมูลอ่อนไหว)
 */
export function visibleFields(
  schema: FormSchema,
  data: FormData,
  hideSensitive = false,
): VisibleField[] {
  const out: VisibleField[] = [];

  for (const section of schema.sections) {
    if (hideSensitive && section.sensitive) continue;
    if (!evalCondition(section.showIf, data)) continue;

    for (const field of section.fields) {
      if (DISPLAY_ONLY_TYPES.includes(field.type)) continue;
      if (!evalCondition(field.showIf, data)) continue;
      out.push({ field, sectionKey: section.key });
    }
  }
  return out;
}

/** ค้นฟิลด์ตาม key ทั้งระดับบนและในตัว repeater */
export function findField(schema: FormSchema, key: string): FormField | undefined {
  for (const s of schema.sections) {
    for (const f of s.fields) {
      if (f.key === key) return f;
    }
  }
  return undefined;
}

/** key ทั้งหมดในฟอร์ม รวม `parent.child` ของ repeater */
export function allFieldKeys(schema: FormSchema): string[] {
  const keys: string[] = [];
  for (const s of schema.sections) {
    for (const f of s.fields) {
      if (DISPLAY_ONLY_TYPES.includes(f.type)) continue;
      keys.push(f.key);
      for (const child of f.fields ?? []) keys.push(`${f.key}.${child.key}`);
    }
  }
  return keys;
}
